<template>
	<PageWrapper>
		<HeroesResidential
			:hero="hero"
			:tracking-location="RESIDENTIAL_PAGE_LOCATIONS.hero"
		/>

		<VectorsMobileFiber />
		<VectorsAnimatedFiber
			:length="1800"
		/>
		<SectionContentContainer classes="-mt-16 pb-12 lg:pb-16">
			<template #items>
				<LazyVisibleSectionContentItem
					v-for="(item, index) in $tm('residential.section-items')"
					:key="item"
					:copy="item"
					:viewbox="RESIDENTIAL_SECTION_ITEMS[index]?.viewbox"
					:image="RESIDENTIAL_SECTION_ITEMS[index]?.image"
					:lines="RESIDENTIAL_SECTION_ITEMS[index]?.lines"
					:circles="RESIDENTIAL_SECTION_ITEMS[index]?.circles"
					hydrate-on-visible
					:lazy="index > 0"
					class="-mt-4 md:-mt-0"
					:ui="{
						leading: 'h-2'
					}"
				/>
			</template>
		</SectionContentContainer>
		<div>
			<LazyPageDivider />
			<InstallationCta />
			<LazyVisibleTestimonials
				hydrate-on-visible
				:tracking-location="RESIDENTIAL_PAGE_LOCATIONS.testimonials"
			/>
			<LazyVisibleFaqs
				hydrate-on-visible
				:tracking-location="RESIDENTIAL_PAGE_LOCATIONS.faq"
				:faqs="faqs"
				divider="both"
			/>
			<LazyVisibleBlogCarousel
				id="residential-page"
				:tracking-location="RESIDENTIAL_PAGE_LOCATIONS.blogCarousel"
				hydrate-on-visible
			/>
			<LazyVisibleBannersAddressChecker
				hydrate-on-visible
				:tracking-location="RESIDENTIAL_PAGE_LOCATIONS.ctaBanner"
			/>
		</div>
	</PageWrapper>
</template>

<script setup lang="ts">
import { RESIDENTIAL_SECTION_ITEMS } from '@/constants/components/residential-sections'
import { RESIDENTIAL_PAGE_LOCATIONS } from '@/constants/ga4/tracking-ids/pages/residential-internet'
import {
	BASE_URL,
	COMPANY_NAME,
	COMPANY_PHONE
} from '@/constants/index.js'
import {
	JSON_LD_HAS_OFFER_CATALOG,
	JSON_LD_LOGO,
	JSON_LD_ORGANIZATION_AREA_SERVED
} from '@/constants/json-ld'
import { useRoute } from 'vue-router'

import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'
import { OG_IMAGE_RESIDENTIAL } from '@/constants/og-image'
import { mapFaqsForJsonLd } from '@/utils/mapFaqsForJsonLd'

const { getStory } = useStoryblokWithErrorReporting()

const route = useRoute()

const { t } = useI18n({
	useScope: 'global'
})

const storyblok = await getStory('residential-internet')

const { story } = storyblok.value?.data || {}

const hero = computed(() => {
	return story?.content?.hero?.[0]
})

const faqs = computed(() => {
	return story?.content?.faqContent
})

const jsonLdFaqs = computed(() => {
	return mapFaqsForJsonLd(faqs.value)
})

useHead({
	title: t('residential.meta-title'),
	meta: [
		{ name: 'description',
			content: t('residential.meta-description', { COMPANY_NAME }) },
		{ name: 'url',
			content: `${BASE_URL}/installation-overview` }
	],
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicons/favicon.ico'
		}
	]
})
const {
	title, description
} = story?.content?.metatags || {
	title: t('residential.meta-title'),
	description: t('residential.meta-description')
}

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogUrl: () => BASE_URL,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => '/images/og-image-fallback.png',
	twitterCard: () => 'summary_large_image'
})

 import.meta.prerender && defineOgImage('Default', OG_IMAGE_RESIDENTIAL)

useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'BreadcrumbList',
			'itemListElement': [
				{
					'@type': 'ListItem',
					'position': 1,
					'name': 'Home',
					'item': BASE_URL
				}, {
					'@type': 'ListItem',
					'position': 2,
					'name': 'Residential Internet',
					'item': `${BASE_URL}${route.fullPath}`
				}
			]
		},
		{
			'@type': 'Service',
			'name': COMPANY_NAME,
			'logo': JSON_LD_LOGO,
			'provider': {
				'@type': 'Organization',
				'name': COMPANY_NAME,
				'telephone': COMPANY_PHONE
			},
			'serviceType': 'Fiber-Optic Internet Access',
			'serviceOutput': 'Internet service',
			'areaServed': JSON_LD_ORGANIZATION_AREA_SERVED,
			'hasOfferCatalog': JSON_LD_HAS_OFFER_CATALOG
		},
		{
			'@type': 'FAQPage',
			'mainEntity': jsonLdFaqs.value
		}
	]
})
</script>
